(() => {
  'use strict';

  const prompt = document.querySelector('[data-newsletter-prompt]');
  const newsletterForms = Array.from(document.querySelectorAll('[data-newsletter-form]'));

  if (!(prompt instanceof HTMLElement) || newsletterForms.length === 0 || window.Shopify?.designMode) return;

  const closeButton = prompt.querySelector('[data-newsletter-prompt-close]');
  const actionButtons = Array.from(prompt.querySelectorAll('[data-newsletter-prompt-action]'));
  const storageKey = 'ww-learning-list-prompt-seen-v1';
  const remainingTimeKey = 'ww-learning-list-prompt-remaining-v1';
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const configuredDelay = Number.parseInt(prompt.dataset.newsletterPromptDelay || '', 10);
  const defaultDelay = Number.isFinite(configuredDelay) ? Math.max(0, configuredDelay) : 60000;
  let timerId = null;
  let timerStartedAt = 0;
  let closeTimerId = null;
  let handled = false;

  const readRemainingTime = () => {
    try {
      const storedTime = Number.parseFloat(window.sessionStorage.getItem(remainingTimeKey) || '');
      return Number.isFinite(storedTime) ? Math.max(0, Math.min(defaultDelay, storedTime)) : defaultDelay;
    } catch (error) {
      return defaultDelay;
    }
  };

  let remainingTime = readRemainingTime();

  const hasBeenSeen = () => {
    try {
      return window.sessionStorage.getItem(storageKey) === '1';
    } catch (error) {
      return false;
    }
  };

  const markAsSeen = () => {
    try {
      window.sessionStorage.setItem(storageKey, '1');
      window.sessionStorage.removeItem(remainingTimeKey);
    } catch (error) {
      // The prompt still remains single-use for the current page when storage is unavailable.
    }
  };

  const storeRemainingTime = () => {
    try {
      window.sessionStorage.setItem(remainingTimeKey, String(Math.ceil(remainingTime)));
    } catch (error) {
      // Visible browsing time still remains accurate for the current page when storage is unavailable.
    }
  };

  const getSignupRegion = (form) =>
    form.closest('[data-newsletter-signup], [data-article-newsletter], .footer-block__newsletter') || form;

  const isVisible = (element) => {
    if (!(element instanceof HTMLElement)) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number.parseFloat(style.opacity) === 0) return false;

    const rectangle = element.getBoundingClientRect();
    return rectangle.width > 0 && rectangle.height > 0 && rectangle.bottom > 0 && rectangle.top < window.innerHeight;
  };

  const visitorHasUsedSignup = () =>
    newsletterForms.some((form) => {
      const emailField = form.querySelector('input[type="email"]');
      const hasEmailValue = emailField instanceof HTMLInputElement && emailField.value.trim().length > 0;
      const hasResult = Boolean(form.querySelector('[data-newsletter-success], [aria-invalid="true"]'));
      return hasEmailValue || hasResult;
    });

  const signupIsAlreadyVisible = () => newsletterForms.some((form) => isVisible(getSignupRegion(form)));

  const anotherOverlayIsOpen = () =>
    Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], .menu-opening')).some(isVisible);

  const restoreMainFocus = () => {
    const main = document.querySelector('main');
    if (main instanceof HTMLElement) main.focus({ preventScroll: true });
  };

  const finishClosing = (restoreFocus) => {
    prompt.hidden = true;
    prompt.inert = true;
    prompt.dataset.state = 'closed';
    closeTimerId = null;

    if (restoreFocus) requestAnimationFrame(restoreMainFocus);
  };

  const hidePrompt = ({ restoreFocus = false } = {}) => {
    if (prompt.hidden) return;

    prompt.inert = true;
    prompt.dataset.state = 'closing';

    if (closeTimerId !== null) window.clearTimeout(closeTimerId);

    if (motionQuery.matches) {
      finishClosing(restoreFocus);
    } else {
      closeTimerId = window.setTimeout(() => finishClosing(restoreFocus), 220);
    }
  };

  const stopTimer = () => {
    if (timerId === null) return;

    window.clearTimeout(timerId);
    timerId = null;
    remainingTime = Math.max(0, remainingTime - (performance.now() - timerStartedAt));
    if (!handled) storeRemainingTime();
  };

  const suppressPrompt = () => {
    stopTimer();
    handled = true;
    markAsSeen();
    hidePrompt();
  };

  const showPrompt = () => {
    timerId = null;
    remainingTime = 0;

    if (handled) return;
    if (visitorHasUsedSignup() || signupIsAlreadyVisible()) {
      suppressPrompt();
      return;
    }
    if (anotherOverlayIsOpen()) {
      remainingTime = 10000;
      storeRemainingTime();
      startTimer();
      return;
    }

    handled = true;
    markAsSeen();
    prompt.hidden = false;
    prompt.inert = false;

    requestAnimationFrame(() => {
      prompt.dataset.state = 'open';
    });
  };

  const startTimer = () => {
    if (handled || timerId !== null || document.visibilityState !== 'visible') return;

    if (remainingTime <= 0) {
      showPrompt();
      return;
    }

    timerStartedAt = performance.now();
    timerId = window.setTimeout(showPrompt, remainingTime);
  };

  const goToSignup = () => {
    const targetForm = newsletterForms[0];
    const targetRegion = getSignupRegion(targetForm);
    const emailField = targetForm.querySelector('input[type="email"]');
    const scrollBehaviour = motionQuery.matches ? 'auto' : 'smooth';

    markAsSeen();
    hidePrompt();
    targetRegion.scrollIntoView({ behavior: scrollBehaviour, block: 'center' });

    window.setTimeout(
      () => {
        if (emailField instanceof HTMLInputElement) emailField.focus({ preventScroll: true });
      },
      motionQuery.matches ? 0 : 420
    );
  };

  newsletterForms.forEach((form) => {
    form.addEventListener('focusin', suppressPrompt, { once: true });
    form.addEventListener('input', suppressPrompt, { once: true });
    form.addEventListener('submit', suppressPrompt, { once: true });
  });

  closeButton?.addEventListener('click', (event) => {
    markAsSeen();
    hidePrompt({ restoreFocus: event.detail === 0 });
  });

  actionButtons.forEach((actionButton) => actionButton.addEventListener('click', goToSignup));

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || prompt.hidden) return;

    const focusIsInsidePrompt = prompt.contains(document.activeElement);
    markAsSeen();
    hidePrompt({ restoreFocus: focusIsInsidePrompt });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      startTimer();
    } else {
      stopTimer();
    }
  });

  window.addEventListener('pagehide', stopTimer);
  window.addEventListener('pageshow', startTimer);

  if (hasBeenSeen()) {
    handled = true;
  } else {
    startTimer();
  }
})();
